import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { RadarChart } from '../components/charts/RadarChart';
import { IncompleteQuizzesCard } from '../components/IncompleteQuizzesCard';
import { SubscribeButton } from '../components/SubscribeButton';
import { SubscriptionStatus } from '../components/SubscriptionStatus';
import { isFreeBeta } from '../utils/quizAccess';
import { UserRole, UserPerformance, QuizAttempt } from '../types';
import { getIncompleteQuizzes } from '../services/quizService';
import { useSyllabus } from '../contexts/SyllabusContext';
import { getDashboardData } from '../services/dashboardService';
import {
  Card,
  CardBody,
  Section,
  Button,
  Link,
  StatCard,
  Badge,
  CollapsibleSection
} from '../components/BaseComponents';

/**
 * Dashboard Component
 * 
 * Serves as the main landing page for the math tutoring application, providing different views
 * based on user role (student or teacher).
 * 
 * Features:
 * - Authentication with role selection (student/teacher)
 * - Student dashboard with progress statistics and performance visualizations
 * - Performance analysis using radar charts for subjects and topics
 * - Year and subject filtering for detailed performance insights
 * - Recent quizzes display with links to all available quizzes
 * - Teacher tools access for class management and student progress tracking
 * 
 * The component dynamically renders content based on authentication status and user role,
 * providing a personalized experience for both students and teachers.
 */
export const Dashboard: React.FC = () => {
  let { currentUser, isStudent, isTeacher, hasQuizAccess, needsSubscription, login, logout } = useUser();
  // Removed deprecated quiz fetching state - now using syllabus navigation
  // TODO: The typing should all come from the type file
  const [performanceData, setPerformanceData] = useState<(UserPerformance & {
    trends?: { completedQuizzesTrend: { value: number; isPositive: boolean }; averageScoreTrend: { value: number; isPositive: boolean }; streakTrend: { value: number; isPositive: boolean }; };
    currentStreak?: number;
    realStrandPerformances?: Array<{ strandId: string, strandName: string, averageScore: number, totalQuizzes: number }>;
    substrandPerformances?: Array<{ substrandId: string, substrandName: string, strandId: string, averageScore: number, totalQuizzes: number }>;
    contentPointPerformances?: Array<{ contentPointId: string, contentDescription: string, averageScore: number, totalQuizzes: number }>;
  }) | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginRole, setLoginRole] = useState('student');

  // Incomplete quiz notification state
  const [incompleteQuizzes, setIncompleteQuizzes] = useState<QuizAttempt[]>([]);
  const [showIncompleteNotification, setShowIncompleteNotification] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  // Collapsible section state
  const [strandPerformanceExpanded, setStrandPerformanceExpanded] = useState(false);
  const [substrandPerformanceExpanded, setSubstrandPerformanceExpanded] = useState(false);
  const [contentPointPerformanceExpanded, setContentPointPerformanceExpanded] = useState(false);

  // Use SyllabusContext for navigation
  const {
    years,
    selectedYear,
    selectedStage,
    selectedStrand,
    selectedSubstrand,
    loadYears,
    selectYear,
    selectStage,
    selectStrand,
    selectSubstrand,
  } = useSyllabus();

  // Load syllabus years on component mount
  useEffect(() => {
    loadYears();
  }, [loadYears]);

  useEffect(() => {
    // Load user-specific performance data
    const loadDashboardData = async () => {
      if (currentUser) {
        setDashboardLoading(true);
        setDashboardError(null);

        try {
          const data = await getDashboardData(currentUser.userId.toString());

          // dashboardService.ts now returns UserPerformance directly
          setPerformanceData(data);
        } catch (error) {
          console.error('Error loading dashboard data:', error);
          setDashboardError('Failed to load dashboard data. Please try again.');

          // Fallback to empty data instead of failing completely
          const fallbackData: UserPerformance & {
            trends?: { completedQuizzesTrend: { value: number; isPositive: boolean }; averageScoreTrend: { value: number; isPositive: boolean }; streakTrend: { value: number; isPositive: boolean }; };
            currentStreak?: number;
            realStrandPerformances?: Array<{ strandId: string, strandName: string, averageScore: number, totalQuizzes: number }>;
            substrandPerformances?: Array<{ substrandId: string, substrandName: string, strandId: string, averageScore: number, totalQuizzes: number }>;
            contentPointPerformances?: Array<{ contentPointId: string, contentDescription: string, averageScore: number, totalQuizzes: number }>;
          } = {
            userId: currentUser.userId,
            strandPerformances: [],
            overallPercentage: 0,
            totalQuizAttempts: 0,
            lastActivityDate: new Date(),
            trends: undefined,
            currentStreak: 0,
            realStrandPerformances: [],
            substrandPerformances: [],
            contentPointPerformances: []
          };
          setPerformanceData(fallbackData);
        } finally {
          setDashboardLoading(false);
        }
      }
    };

    loadDashboardData();
  }, [currentUser]);

  // Check for incomplete quizzes when user logs in
  useEffect(() => {
    const checkIncompleteQuizzes = async () => {
      if (currentUser && isStudent()) {
        try {
          const incomplete = await getIncompleteQuizzes(currentUser.userId);
          setIncompleteQuizzes(incomplete);

          // Show notification if user has incomplete quizzes
          if (incomplete.length > 0) {
            setShowIncompleteNotification(true);

            // Auto-hide notification after 5 seconds
            setTimeout(() => {
              setShowIncompleteNotification(false);
            }, 5000);
          }
        } catch (error) {
          console.error('Failed to fetch incomplete quizzes:', error);
        }
      }
    };

    checkIncompleteQuizzes();
  }, [currentUser, isStudent]);

  // Prepare strand radar chart data based on selected year
  const getStrandRadarDataForYear = () => {
    if (!performanceData || !selectedStage) return { labels: [], data: [] };

    // Get strands for the selected stage
    const strandNames = selectedStage.strands?.map(s => s.strandName) || [];

    if (strandNames.length === 0) {
      return { labels: [], data: [] };
    }

    // Use real performance data if available
    const strandPerformances = performanceData.realStrandPerformances || [];
    const data = strandNames.map(strandName => {
      const strandPerf = strandPerformances.find(sp => sp.strandName === strandName);
      return strandPerf ? strandPerf.averageScore : 0;
    });

    return {
      labels: strandNames,
      data
    };
  };

  // Prepare substrand radar chart data based on selected strand
  const getSubstrandRadarData = () => {
    if (!performanceData || !selectedStrand) return { labels: [], data: [] };

    // Get substrands for the selected strand
    const substrandNames = selectedStrand.substrands?.map(ss => ss.substrandName) || [];

    if (substrandNames.length === 0) {
      return { labels: [], data: [] };
    }

    // Use real performance data if available
    const substrandPerformances = performanceData.substrandPerformances || [];
    const data = substrandNames.map(substrandName => {
      const substrandPerf = substrandPerformances.find(ssp =>
        ssp.substrandName === substrandName && ssp.strandId === selectedStrand.strandId
      );
      return substrandPerf ? substrandPerf.averageScore : 0;
    });

    return {
      labels: substrandNames,
      data
    };
  };

  // Prepare content point radar chart data based on selected substrand
  const getContentPointRadarDataForSubstrand = () => {
    if (!performanceData || !selectedSubstrand) return { labels: [], data: [] };

    // Get content points for the selected substrand
    const contentPoints = selectedSubstrand.contentPoints || [];
    const contentPointDescriptions = contentPoints.map(cp =>
      cp.contentDescription.substring(0, 30) + '...'
    );

    if (contentPointDescriptions.length === 0) {
      return { labels: [], data: [] };
    }

    // Use real performance data if available
    const contentPointPerformances = performanceData.contentPointPerformances || [];
    const data = contentPoints.map(cp => {
      const contentPointPerf = contentPointPerformances.find(cpp =>
        cpp.contentPointId === cp.contentPointId
      );
      return contentPointPerf ? contentPointPerf.averageScore : 0;
    });

    return {
      labels: contentPointDescriptions,
      data
    };
  };

  // Calculate overall stats
  const getOverallStats = () => {
    if (!performanceData) return { completed: 0, avgScore: 0, streak: 0 };

    return {
      completed: performanceData.totalQuizAttempts || 0,
      avgScore: performanceData.overallPercentage || 0,
      streak: performanceData.currentStreak || 0
    };
  };

  const stats = getOverallStats();

  // Get stages for the selected year
  const getStagesForYear = () => {
    if (!selectedYear) return [];
    const yearData = years.find(y => y.yearNumber === selectedYear);
    return yearData?.stages || [];
  };

  // Get strands for the selected stage
  const getStrandsForStage = () => {
    return selectedStage?.strands || [];
  };

  // Get substrands for the selected strand
  const getSubstrandsForStrand = () => {
    return selectedStrand?.substrands || [];
  };

  const handleLogin = () => {

    const newUser = {
      firstName: '',
      lastName: '',
      email: loginEmail,
      password: 'password',
      role: loginRole as UserRole // Cast the string to UserRole type
    };

    login(newUser.email, newUser.password);

    setShowLoginModal(false);

    // Reset form
    setLoginEmail('');
    setLoginRole('student');
  };

  return (
    <div className="min-h-screen ">

      {/* Main content */}
      <div className="container mx-auto py-8 px-4">

        {/* Incomplete Quiz Notification Banner */}
        {showIncompleteNotification && incompleteQuizzes.length > 0 && isStudent() && (
          <Section className="mb-8">
            <Card className="border-2 border-orange-200 bg-orange-50" noHover={true}>
              <CardBody>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="icon-circle-sm bg-orange-100">
                      <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-orange-900">
                        Resume Your Quizzes
                      </h3>
                      <p className="text-orange-800">
                        You have {incompleteQuizzes.length} incomplete quiz{incompleteQuizzes.length !== 1 ? 'es' : ''} waiting for you.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="warning" className="bg-orange-200 text-orange-800">
                      {incompleteQuizzes.length} pending
                    </Badge>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowIncompleteNotification(false)}
                      className="text-orange-600 hover:text-orange-800"
                    >
                      ✕
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Section>
        )}

        {/* Stats for students */}
        {isStudent() && hasQuizAccess() && (
          <Section title="Your Progress" className="mb-8" titleClassName="text-lg font-medium text-neutral-700">
            {dashboardError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700">{dashboardError}</p>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  className="mt-2 text-sm"
                >
                  Retry
                </Button>
              </div>
            )}

            {dashboardLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="loading-spinner"></div>
                <span className="ml-3 text-neutral-500">Loading your progress...</span>
              </div>
            )}

            {!dashboardLoading && performanceData && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card-stats">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-neutral-600">Quizzes Completed</div>
                    <div className="text-lg">📚</div>
                  </div>
                  <div className="text-2xl font-semibold text-neutral-900">{stats.completed}</div>
                  {performanceData?.trends?.completedQuizzesTrend && (
                    <div className={`text-sm mt-1 flex items-center ${performanceData.trends.completedQuizzesTrend.isPositive ? 'text-green-600' : 'text-red-600'
                      }`}>
                      <span className="mr-1">
                        {performanceData.trends.completedQuizzesTrend.isPositive ? '↗' : '↘'}
                      </span>
                      {Math.abs(performanceData.trends.completedQuizzesTrend.value)}% from last month
                    </div>
                  )}
                </div>
                <div className="card-stats">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-neutral-600">Average Score</div>
                    <div className="text-lg">📈</div>
                  </div>
                  <div className="text-2xl font-semibold text-neutral-900">{stats.avgScore}%</div>
                  {performanceData?.trends?.averageScoreTrend && (
                    <div className={`text-sm mt-1 flex items-center ${performanceData.trends.averageScoreTrend.isPositive ? 'text-green-600' : 'text-red-600'
                      }`}>
                      <span className="mr-1">
                        {performanceData.trends.averageScoreTrend.isPositive ? '↗' : '↘'}
                      </span>
                      {Math.abs(performanceData.trends.averageScoreTrend.value)}% from last month
                    </div>
                  )}
                </div>
                <div className="card-stats">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm text-neutral-600">Current Streak</div>
                    <div className="text-lg">🔥</div>
                  </div>
                  <div className="text-2xl font-semibold text-neutral-900">{stats.streak} days</div>
                  {performanceData?.trends?.streakTrend && (
                    <div className={`text-sm mt-1 flex items-center ${performanceData.trends.streakTrend.isPositive ? 'text-green-600' : 'text-red-600'
                      }`}>
                      <span className="mr-1">
                        {performanceData.trends.streakTrend.isPositive ? '↗' : '↘'}
                      </span>
                      {Math.abs(performanceData.trends.streakTrend.value)}% from last month
                    </div>
                  )}
                </div>
              </div>
            )}
          </Section>
        )}

        {/* Subscription Status Section */}
        {isStudent() && !isFreeBeta() && (currentUser?.subscriptionStatus || currentUser?.trialExpiry) && (
          <Section title="Account Status" className="mb-8" titleClassName="text-lg font-medium text-neutral-700">
            <SubscriptionStatus />
          </Section>
        )}

        {/* Subscription Required Section */}
        {isStudent() && needsSubscription() && (
          <Section title="Access Required" className="mb-8" titleClassName="text-lg font-medium text-neutral-700">
            <Card className="border-2 border-blue-200 bg-blue-50" noHover={true}>
              <CardBody>
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div className="mb-4 md:mb-0 text-center md:text-left">
                    <h3 className="text-lg font-medium text-blue-900 mb-2">Your trial has expired</h3>
                    <p className="text-blue-800">
                      Subscribe to continue accessing quizzes and tracking your progress.
                    </p>
                  </div>
                  <SubscribeButton variant="large" />
                </div>
              </CardBody>
            </Card>
          </Section>
        )}

        {/* Quick Quiz Generation */}
        {isStudent() && hasQuizAccess() && (
          <Section title="Start a Quiz" className="mb-8" titleClassName="text-lg font-medium text-neutral-700">
            <Link to="/categories" className="no-underline hover:no-underline">
              <Card className="border-2 border-primary-400 hover:border-primary-500 transition-colors cursor-pointer group">
                <CardBody>
                  <div className="flex flex-col md:flex-row items-center justify-between">
                    <div className="mb-4 md:mb-0">
                      <h3 className="text-lg font-medium text-neutral-900 mb-2">Generate Custom Quizzes</h3>
                      <p className="text-neutral-600">Browse the curriculum and create quizzes from any topic or content area.</p>
                    </div>
                    <div className="bg-primary text-white px-4 py-2 rounded-lg font-medium">
                      Browse Quizzes
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Link>
          </Section>
        )}

        {/* My Stats section with year and subject selection */}
        {isStudent() && hasQuizAccess() && performanceData && (
          <Section title="Detailed Stats" className="mb-8" titleClassName="text-lg font-medium text-neutral-700">
            <Card className="border border-neutral-200" noHover={true}>
              <CardBody>
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <label htmlFor="year-select" className="form-label">
                      Year Level
                    </label>
                    <select
                      id="year-select"
                      value={selectedYear || ''}
                      onChange={(e) => selectYear(e.target.value)}
                      className="form-select"
                    >
                      <option value="">Select Year</option>
                      {years.map(year => (
                        <option key={year.yearNumber} value={year.yearNumber}>
                          Year {year.yearNumber}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedYear && getStagesForYear().length > 0 && (
                    <div className="flex-1">
                      <label htmlFor="stage-select" className="form-label">
                        Select Stage
                      </label>
                      <select
                        id="stage-select"
                        value={selectedStage?.stageId || ''}
                        onChange={(e) => {
                          const stage = getStagesForYear().find(s => s.stageId === e.target.value);
                          if (stage) selectStage(stage);
                        }}
                        className="form-select"
                      >
                        <option value="">Select Stage</option>
                        {getStagesForYear().map(stage => (
                          <option key={stage.stageId} value={stage.stageId}>
                            {stage.stageName || stage.stageCode}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Strand Performance Chart for Selected Stage */}
                {selectedStage && (
                  <CollapsibleSection
                    title={`Strand Performance in ${selectedStage.stageName || selectedStage.stageCode}`}
                    isExpanded={strandPerformanceExpanded}
                    onToggle={() => setStrandPerformanceExpanded(!strandPerformanceExpanded)}
                  >
                    {/* Strand Selector */}
                    {selectedStage && getStrandsForStage().length > 0 && (
                      <div className="mb-6">
                        <label htmlFor="strand-select" className="form-label">
                          Select Strand
                        </label>
                        <select
                          id="strand-select"
                          value={selectedStrand?.strandId || ''}
                          onChange={(e) => {
                            const strand = getStrandsForStage().find(s => s.strandId === e.target.value);
                            if (strand) selectStrand(strand);
                          }}
                          className="form-select"
                        >
                          <option value="">Select Strand</option>
                          {getStrandsForStage().map(strand => (
                            <option key={strand.strandId} value={strand.strandId}>
                              {strand.strandName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="h-[500px] border-t pt-4 border-gray-200">
                      <RadarChart
                        labels={getStrandRadarDataForYear().labels}
                        data={getStrandRadarDataForYear().data}
                        title={`Strand Performance (%)`}
                      />
                    </div>
                  </CollapsibleSection>
                )}



                {/* Substrand Performance Chart */}
                {selectedStrand && (
                  <CollapsibleSection
                    title={`Substrand Performance in ${selectedStrand.strandName}`}
                    isExpanded={substrandPerformanceExpanded}
                    onToggle={() => setSubstrandPerformanceExpanded(!substrandPerformanceExpanded)}
                  >
                    {/* Substrand Selector */}
                    {selectedStrand && getSubstrandsForStrand().length > 0 && (
                      <div className="mb-6">
                        <label htmlFor="substrand-select" className="form-label">
                          Select Substrand
                        </label>
                        <select
                          id="substrand-select"
                          value={selectedSubstrand?.substrandId || ''}
                          onChange={(e) => {
                            const substrand = getSubstrandsForStrand().find(s => s.substrandId === e.target.value);
                            if (substrand) selectSubstrand(substrand);
                          }}
                          className="form-select"
                        >
                          <option value="">Select Substrand</option>
                          {getSubstrandsForStrand().map(substrand => (
                            <option key={substrand.substrandId} value={substrand.substrandId}>
                              {substrand.substrandName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="h-[500px] border-t pt-4 border-gray-200">
                      <RadarChart
                        labels={getSubstrandRadarData().labels}
                        data={getSubstrandRadarData().data}
                        title={`Substrand Performance (%)`}
                      />
                    </div>
                  </CollapsibleSection>
                )}

                {/* Content Point Performance Chart */}
                {selectedSubstrand && (
                  <CollapsibleSection
                    title={`Content Point Performance for ${selectedSubstrand.substrandName}`}
                    isExpanded={contentPointPerformanceExpanded}
                    onToggle={() => setContentPointPerformanceExpanded(!contentPointPerformanceExpanded)}
                    className="mt-8"
                  >
                    <div className="h-[500px] border-t pt-4 border-gray-200">
                      <RadarChart
                        labels={getContentPointRadarDataForSubstrand().labels}
                        data={getContentPointRadarDataForSubstrand().data}
                        title={`Content Point Performance (%)`}
                      />
                    </div>
                  </CollapsibleSection>
                )}
              </CardBody>
            </Card>
          </Section>
        )}

        {/* Incomplete Quizzes Section */}
        {isStudent() && hasQuizAccess() && (
          <Section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-lg font-medium text-neutral-700">Resume Previous Quizzes</h2>
              {incompleteQuizzes.length > 0 && (
                <span className="bg-neutral-100 text-neutral-600 px-2 py-1 rounded text-sm">
                  {incompleteQuizzes.length} pending
                </span>
              )}
            </div>
            <IncompleteQuizzesCard
              onQuizResumed={() => {
                // Refresh incomplete quizzes count
                if (currentUser) {
                  getIncompleteQuizzes(currentUser.userId).then(setIncompleteQuizzes);
                }
              }}
            />
          </Section>
        )}

        {/* Quiz History Section */}
        {isStudent() && hasQuizAccess() && (
          <Section title="Quiz History" className="mb-8" titleClassName="text-lg font-medium text-neutral-700">
            <Link to="/history" className="no-underline hover:no-underline">
              <Card className="border border-neutral-200 hover:border-neutral-300 transition-colors cursor-pointer group">
                <CardBody>
                  <div className="flex flex-col md:flex-row items-center justify-between">
                    <div className="mb-4 md:mb-0">
                      <h3 className="text-lg font-medium text-neutral-900 mb-2">
                        View All Completed Quizzes
                      </h3>
                      <p className="text-neutral-600">
                        Review your progress, see detailed results, and track your improvement over time.
                      </p>
                    </div>
                    <div className="bg-neutral-100 text-neutral-700 px-4 py-2 rounded-lg font-medium">
                      View  Quiz History
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Link>
          </Section>
        )}

        {/* Teacher section */}
        {isTeacher() && (
          <Section title="Teacher Tools" titleClassName="text-lg font-medium text-neutral-700">
            <Card className="border border-neutral-200" noHover={true}>
              <CardBody className="flex flex-col md:flex-row items-center justify-between">
                <div className="mb-4 md:mb-0">
                  <h3 className="text-lg font-medium text-neutral-900 mb-2">Manage Your Classes</h3>
                  <p className="text-neutral-600">Create quizzes, track student progress, and manage your classes.</p>
                </div>
                <Link to="/teacher" className="bg-primary text-white px-4 py-2 rounded-lg font-medium no-underline">
                  Teacher Dashboard
                </Link>
              </CardBody>
            </Card>
          </Section>
        )}
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="modal-overlay">
          <div className="bg-white rounded-lg p-8 max-w-md w-full shadow-xl">
            <h2 className="text-2xl font-bold text-indigo-700 mb-6">Log In</h2>

            <div className="mb-4">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className="form-input"
                placeholder="your@email.com"
              />
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowLoginModal(false)}
                className="border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleLogin}
                className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
                disabled={!loginEmail}
              >
                Log In
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
